import Cookies from "js-cookie";
import React, { useEffect, useMemo, useState } from "react";
import { CiEdit } from "react-icons/ci";
import {
  FaChevronDown,
  FaChevronUp,
  FaSearch,
  FaUser
} from "react-icons/fa";
import { useNavigate } from "react-router";
import { useGlobalContext } from "../../../Context/GlobalContext";
import { usePayrollContext } from "../../../Context/PayrollContext";

const VerifySalary = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { openToast, baseUrl, setIsLoading, isLoading } = useGlobalContext();
  const { selectAllInput, monthYear, calculateDailySalary } = usePayrollContext();
  const [staffsSalaryData, setStaffsSalaryData] = useState([]);

  const [finalizedPayroll, setFinalizedPayroll] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate()

  async function updateFinalizedPayroll(e) {
    setIsLoading(true);
    try {
      const response = await fetch(`${baseUrl}payroll/allStaff/finalize`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get("flowChangerAuthToken")}`,
        },
        body: JSON.stringify(finalizedPayroll),
      });

      console.log("Response from backend:", response);

      if (response.ok) {
        const result = await response.json();
        console.log("Response data:", result);
        openToast("Salary details successfully updated or created", "success");
      } else {
        openToast("An error occurred while adding or updating Salary", "error");
      }
    } catch (error) {
      console.error("Error submitting Salary:", error);
      openToast("An error occurred while adding or updating Salary", "error");
    } finally {
      setIsLoading(false);
      navigate("/payroll-menu?tab=1")
    }
  }

  useEffect(() => {
    setStaffsSalaryData(selectAllInput?.map((salaryData) => ({
      id: salaryData?.id,
      name: salaryData?.name,
      earnings: salaryData?.staffDetails?.Earning?.filter(({ salary_month }) => salary_month === monthYear),
      deduction: salaryData?.staffDetails?.Deduction?.filter(({ deduction_month }) => deduction_month === monthYear),
      employeeContribution: salaryData?.staffDetails?.EmployeeContribution?.filter(({ contribution_month }) => contribution_month === monthYear),
      employerContribution: salaryData?.staffDetails?.EmployerContribution?.filter(({ contribution_month }) => contribution_month === monthYear),
      salaryDetails: salaryData?.staffDetails?.SalaryDetails[0],
      punchRecords: salaryData.staffDetails?.PunchRecords,
      leavePolicy: salaryData?.staffDetails?.LeavePolicy,
    })));
  }, [selectAllInput]);

  function calculateTotalFineAndOvertime(punchRecords) {
    let totalFine = 0;
    let totalOvertime = 0;

    punchRecords?.forEach((record) => {
      if (record.fine) {
        const {
          totalAmount = 0,
          excessBreakAmount = 0,
          lateEntryAmount = 0,
          earlyOutAmount = 0,
        } = record.fine;

        totalFine += totalAmount > 0
          ? totalAmount
          : excessBreakAmount + lateEntryAmount + earlyOutAmount;
      }

      if (record.Overtime) {
        const {
          totalAmount = 0,
          lateOutOvertimeAmount = 0,
          earlyCommingEntryAmount = 0,
        } = record.Overtime;

        totalOvertime += totalAmount > 0
          ? totalAmount
          : lateOutOvertimeAmount + earlyCommingEntryAmount;
      }
    });

    return { totalFine, totalOvertime };
  }

  const computedStaffData = useMemo(() => {
    return staffsSalaryData.map((staff) => {
      const { totalFine, totalOvertime } = calculateTotalFineAndOvertime(staff.punchRecords);
      let otherEarnings = staff?.earnings?.filter(({ heads }) => heads !== "Basic" && heads !== "Overtime")?.reduce((acc, curr) => acc + curr.amount, 0);
      let otherDeductions = staff?.deduction?.filter(({ heads }) => heads !== "Loan Debit")?.reduce((acc, curr) => acc + curr.amount, 0);
      let totalDeductions = staff?.deduction?.reduce((acc, curr) => acc + curr.amount, 0);
      let totalEarnings = staff?.earnings?.reduce((acc, curr) => acc + curr.amount, 0);
      let paidAmount = staff?.salaryDetails?.PaymentHistory?.reduce((acc, curr) => acc + curr.amount, 0);
      let totalReimbursement = staff?.salaryDetails?.Reimbursement?.reduce((acc, curr) => acc + curr.amount, 0);
      let totalIncentive = staff?.salaryDetails?.Incentive?.reduce((acc, curr) => acc + curr.amount, 0);
      let totalEmployerContributions = staff?.employerContribution?.reduce((acc, curr) => acc + curr.amount, 0);
      let totalEmployeeContributions = staff?.employeeContribution?.reduce((acc, curr) => acc + curr.amount, 0);
      let totalComplainces = totalEmployerContributions + totalEmployeeContributions;
      let dailySalary = calculateDailySalary(staff?.salaryDetails?.ctc_amount) || 0;
      let totalWeeklyOffDaysSalary = staff?.punchRecords?.filter((punch) => {
        const date = new Date(punch?.punchDate);
        return date.getDay() === 0;
      })?.length * dailySalary || 0;
      console.log(totalWeeklyOffDaysSalary);
      let totalEarlyLeaveFine = staff?.punchRecords?.map(({ fine }) => fine?.earlyOutAmount || 0).reduce((acc, curr) => acc + curr, 0) || 0;
      let totalLateComingFine = staff?.punchRecords?.map(({ fine }) => fine?.lateEntryAmount || 0).reduce((acc, curr) => acc + curr, 0) || 0;
      let totalPresentDaysSalary = staff?.punchRecords?.filter(({ status }) => status === "PRESENT")?.length * dailySalary || 0;
      let totalHalfDaysSalary = staff?.punchRecords?.filter(({ status }) => status === "HALFDAY")?.length * dailySalary * 0.5 || 0;
      let totalPaidLeaveDaysSalary = staff?.punchRecords?.filter(({ status }) => status === "PAIDLEAVE")?.length * dailySalary || 0;
      console.log(totalPresentDaysSalary);
      let toatalPayableAmount = totalPresentDaysSalary + totalHalfDaysSalary + totalWeeklyOffDaysSalary + totalPaidLeaveDaysSalary + totalReimbursement + totalIncentive + totalOvertime - totalFine;
      totalDeductions = totalDeductions + totalEarlyLeaveFine + totalLateComingFine;

      return {
        ...staff,
        totalEarnings,
        otherEarnings,
        totalDeductions,
        otherDeductions,
        totalReimbursement,
        totalIncentive,
        totalComplainces,
        dailySalary,
        totalWeeklyOffDaysSalary,
        totalEarlyLeaveFine,
        totalLateComingFine,
        totalPresentDaysSalary,
        totalHalfDaysSalary,
        totalPaidLeaveDaysSalary,
        toatalPayableAmount,
        totalEmployerContributions,
        totalEmployeeContributions,
        totalComplainces,
        paidAmount,
      };
    });
  }, [staffsSalaryData]);

  useEffect(() => {
    const payrollData = computedStaffData.map((staff) => ({
      id: staff?.salaryDetails?.id,
      payroll: true,
      salary: staff?.toatalPayableAmount - staff?.paidAmount,
    }));
    setFinalizedPayroll(payrollData);
  }, [computedStaffData]);

  return (
    <div className="bg-white p-4 md:p-6 lg:p-8">
      {/* Header */}
      <h1 className="mb-4 md:mb-6 font-semibold text-[#27004a] text-lg sm:text-xl md:text-2xl">
        Verify Salary
      </h1>
      {/* Search Bar */}
      <div className="relative mb-6">
        <FaSearch className="absolute top-1/2 -translate-y-1/2 left-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search by person name"
          className="py-2 pr-4 pl-12 border focus:border-blue-500 rounded-lg w-full text-sm focus:outline-none"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Table Header */}
      <div className="hidden md:grid grid-cols-7 gap-4 bg-gray-50 mb-4 p-2 sm:p-[12px] rounded-lg text-xs md:text-sm verify-sal">
        <div className="text-[12px] md:text-[14px]">
          <span className="font-medium text-black">EMPLOYEE ID</span>
        </div>
        <div className="text-[12px] md:text-[14px]">
          <span className="font-medium text-black">NAME</span>
        </div>
        <div className="text-[12px] md:text-[14px]">
          <span className="font-medium text-black">CTC / MONTH</span>
        </div>
        <div className="text-[12px] md:text-[14px]">
          <span className="font-medium text-black whitespace-nowrap">
            PAYABLE AMOUNT
          </span>
        </div>
        <div className="text-[12px] md:text-[14px]">
          <span className="font-medium text-black">PAID</span>
        </div>
        <div className="text-[12px] md:text-[14px]">
          <span className="font-medium text-black">REMAINING</span>
        </div>
        <div className="text-[12px] md:text-[14px]">
          <span className="font-medium text-black">ACTION</span>
        </div>
      </div>

      {/* Employee Row */}
      <div className="mb-4 border rounded-lg">
        {computedStaffData?.map(({
          totalEarnings,
          otherEarnings,
          totalDeductions,
          otherDeductions,
          totalReimbursement,
          totalIncentive,
          totalComplainces,
          dailySalary,
          totalWeeklyOffDaysSalary,
          totalEarlyLeaveFine,
          totalLateComingFine,
          totalPresentDaysSalary,
          totalHalfDaysSalary,
          totalPaidLeaveDaysSalary,
          toatalPayableAmount,
          totalEmployerContributions,
          totalEmployeeContributions,
          paidAmount,
          ...staff
        }, index) => {
          return (
            <React.Fragment key={staff?.id}>
              <div
                className="grid grid-cols-1 md:grid-cols-7 gap-4 p-2 sm:p-[14px] border-b last:border-b-0 text-xs md:text-sm cursor-pointer verify-salr"
                onClick={() => setIsExpanded((prev) => prev === staff?.id ? null : staff?.id)}
              >
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <FaUser className="text-gray-400" />
                  <span className="sm:hidden font-medium">ID:</span>
                  <span>{staff?.id?.split("-")?.[0]}</span>
                </div>
                <div className="w-full sm:w-auto">
                  <span className="sm:hidden font-medium">Name: </span>
                  {staff?.name}
                </div>
                <div className="w-full sm:w-auto">
                  <span className="sm:hidden font-medium">CTC/Month: </span>
                  {staff?.salaryDetails?.ctc_amount}
                </div>
                <div className="w-full sm:w-auto">
                  <span className="sm:hidden font-medium">Payable: </span>
                  {parseFloat(toatalPayableAmount).toFixed(2)}
                </div>
                <div className="w-full sm:w-auto">
                  <span className="sm:hidden font-medium">Paid: </span>
                  {parseFloat(paidAmount).toFixed(2) || 0}
                </div>
                <div className="w-full sm:w-auto">
                  <span className="sm:hidden font-medium">Remaining: </span>
                  {parseFloat(toatalPayableAmount - paidAmount).toFixed(2) || 0}
                </div>
                <div className="flex items-center justify-between md:justify-start gap-4 w-full sm:w-auto salary-verify">
                  <button className="flex items-center gap-[8px] border-[#dbdbdb] pt-[6px] pr-[14px] pb-[6px] pl-[14px] border rounded-lg">
                    <CiEdit />
                    Edit
                  </button>
                  {isExpanded === staff?.id ? <FaChevronUp /> : <FaChevronDown />}
                </div>
              </div>
              {isExpanded === staff?.id && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-2 md:p-4 border-t">
                  {/* Left Column */}
                  <div className="space-y-2 bg-[#f3fdf8] p-2 md:p-4 rounded-lg text-xs md:text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium text-[12px] text-black">
                        Earnings
                      </span>
                      <span className="font-medium text-[12px]">{totalEarnings + totalIncentive + totalReimbursement}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[12px] text-gray-600">Basic</span>
                      <span className="text-[12px]">{staff?.earnings?.find(({ heads }) => heads === "Basic")?.amount || "0.00"}</span>
                    </div>
                    <div className="flex justify-between mt-2 md:mt-4">
                      <span className="text-[12px] text-gray-600">Overtime</span>
                      <span className="text-[12px]">{staff?.earnings?.find(({ heads }) => heads === "Overtime")?.amount || "0.00"}</span>
                    </div>
                    <div className="flex justify-between mt-2 md:mt-4">
                      <span className="text-[12px] text-gray-600">
                        Other Earnings
                      </span>
                      <span className="text-[12px]">{otherEarnings === 0 ? "0.00" : otherEarnings}</span>
                    </div>
                    <div className="flex justify-between mt-2 md:mt-4">
                      <span className="text-[12px] text-gray-600">Incentive</span>
                      <span className="text-[12px]">{totalIncentive === 0 ? "0.00" : totalIncentive}</span>
                    </div>
                    <div className="flex justify-between mt-2 md:mt-4">
                      <span className="text-[12px] text-gray-600">Workbasis</span>
                      <span className="text-[12px]">0.00</span>
                    </div>
                    <div className="flex justify-between mt-2 md:mt-4">
                      <span className="text-[12px] text-gray-600">Reimbursement</span>
                      <span className="text-[12px]">{totalReimbursement === 0 ? "0.00" : totalReimbursement}</span>
                    </div>
                  </div>

                  {/* Middle Column */}
                  <div className="bg-[#f3fdf8] p-4 rounded-lg space-y-3  text-xs md:text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium text-[12px] text-black">
                        Employee Contributions
                      </span>
                      <span className="font-medium text-[12px]">{totalEmployeeContributions === 0 ? "0.00" : totalEmployeeContributions}</span>
                    </div>
                    {staff?.employerContribution?.map((contribution) => (
                      <div className="flex justify-between">
                        <span className="text-[12px] text-gray-600">{contribution?.type === "pf" ? "Employee PF" : contribution?.type === "esi" ? "Employee ESI" : contribution?.type === "lwf" ? "Labour Welfare Fund " : "PF EDLI & Admin Charges"}</span>
                        <span className="text-[12px]">{contribution.amount === 0 ? "0.00" : contribution.amount}</span>
                      </div>
                    ))}
                  </div>

                  {/* Third Column */}
                  <div className="space-y-2 md:space-y-0 bg-[#fff1f3] rounded-lg">
                    <div className="space-y-2 mb-2 md:mb-4 p-2 md:p-4 text-xs md:text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium text-[12px] text-black">
                          Compliance Deductions
                        </span>
                        <span className="font-medium text-[12px]">{totalComplainces === 0 ? "0.00" : totalComplainces}</span>
                      </div>
                      {staff?.employerContribution?.map((contribution) => (
                        <div className="flex justify-between">
                          <span className="text-[12px] text-gray-600">{contribution?.type === "pf" ? "Employer PF" : contribution?.type === "esi" ? "Employer ESI" : contribution?.type === "lwf" ? "Labour Welfare Fund " : "PF EDLI & Admin Charges"}</span>
                          <span className="text-[12px]">{contribution.amount === 0 ? "0.00" : contribution.amount}</span>
                        </div>
                      ))}
                      {staff?.employeeContribution?.map((contribution) => (
                        <div className="flex justify-between">
                          <span className="text-[12px] text-gray-600">{contribution?.type === "pf" ? "Employee PF" : contribution?.type === "esi" ? "Employee ESI" : contribution?.type === "lwf" ? "Labour Welfare Fund " : "Professional Tax"}</span>
                          <span className="text-[12px]">{contribution.amount === 0 ? "0.00" : contribution.amount}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Fourth Column */}
                  <div className="space-y-2 md:space-y-0 bg-[#fff1f3] rounded-lg">
                    <div className="space-y-2 mb-2 md:mb-4 p-2 md:p-4 text-xs md:text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium text-[12px] text-black">
                          Total Deductions
                        </span>
                        <span className="font-medium text-[12px]">{totalDeductions === 0 ? "0.00" : parseFloat(totalDeductions).toFixed(2)}</span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-[12px] text-gray-600">Loan Debit</span>
                        <span className="text-[12px]">{staff?.deduction?.find(({ heads }) => heads === "Loan Debit")?.amount || "0.00"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[12px] text-gray-600">
                          Early Leave Fine
                        </span>
                        <span className="text-[12px]">{totalEarlyLeaveFine === 0 ? "0.00" : parseFloat(totalEarlyLeaveFine).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[12px] text-gray-600">
                          Late Coming Fine
                        </span>
                        <span className="text-[12px]">{totalLateComingFine === 0 ? "0.00" : parseFloat(totalLateComingFine).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[12px] text-gray-600">
                          Other Deductions
                        </span>
                        <span className="text-[12px]">{otherDeductions === 0 ? "0.00" : otherDeductions}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          )
        })}
      </div>

      {/* Footer Actions */}
      <div className="flex sm:flex-row flex-col justify-between space-y-2 sm:space-y-0 mt-6 md:mt-8">
        <button className="border-gray-300 px-4 py-2 border rounded-md w-full sm:w-auto text-sm first-btn" onClick={() => navigate(-1)}>Cancel</button>
        <div className="flex sm:flex-row flex-col justify-between items-center gap-2 sm:gap-[10px]">
          <button className="border-gray-300 px-4 py-2 border rounded-md w-full sm:w-auto text-sm first-btn" onClick={() => navigate(-1)}>Back</button>
          <button className="justify-center bg-blue-600 px-4 py-2 rounded-md w-full sm:w-auto text-sm text-white second-btn" onClick={updateFinalizedPayroll}>Finalize Calculation</button>
        </div>
      </div>
    </div>
  );
};

export default VerifySalary;

